import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UIState {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  /** Whether the off-canvas sidebar drawer is open (below the `lg` breakpoint,
   * where the fixed sidebar is hidden and reached via the header hamburger). */
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
  toggleMobileNav: () => void;

  // --- Per-account display preferences (set on /profile) --------------------
  // Persisted client-side (no backend field); each applies instantly. Only
  // these three are persisted (see `partialize`) — the nav flags above stay
  // transient per tab.
  /** Denser table rows (fit more rows per screen). */
  compactTables: boolean;
  setCompactTables: (on: boolean) => void;
  /** Whether the user opted into browser desktop notifications. */
  desktopNotifications: boolean;
  setDesktopNotifications: (on: boolean) => void;
  /** Turn off background animation / non-essential motion. */
  reduceMotion: boolean;
  setReduceMotion: (on: boolean) => void;
}

/** App-wide UI state that isn't tied to one feature. Feature-specific state
 * (e.g. auth) lives in its own `features/<name>/store` instead.
 *
 * The display preferences are persisted to `localStorage` (via `persist` +
 * `partialize`) so a user's choices survive reloads; the transient nav flags
 * are intentionally left out of the persisted slice. */
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: false,
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      mobileNavOpen: false,
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
      toggleMobileNav: () => set((s) => ({ mobileNavOpen: !s.mobileNavOpen })),

      compactTables: false,
      setCompactTables: (on) => set({ compactTables: on }),
      desktopNotifications: false,
      setDesktopNotifications: (on) => set({ desktopNotifications: on }),
      reduceMotion: false,
      setReduceMotion: (on) => set({ reduceMotion: on }),
    }),
    {
      name: "ui-prefs",
      partialize: (state) => ({
        compactTables: state.compactTables,
        desktopNotifications: state.desktopNotifications,
        reduceMotion: state.reduceMotion,
      }),
    },
  ),
);
